import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CircuitBoard, Code2, SlidersHorizontal, Upload } from 'lucide-react';
import ProjectBuilderBoardSizer from './ProjectBuilderBoardSizer';
import ProjectBuilderExamplesManager from './ProjectBuilderExamplesManager';
import ProjectBuilderPassiveManager from './ProjectBuilderPassiveManager';
import { adminProjectBuilderAPI } from '../services/adminApi';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FeaturePanel = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FeatureHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const FeatureTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FeatureStateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${props => props.$enabled ? '#ecfdf5' : '#f8fafc'};
  color: ${props => props.$enabled ? '#047857' : '#475569'};
  border: 1px solid ${props => props.$enabled ? '#a7f3d0' : '#cbd5e1'};
  font-size: 0.78rem;
  font-weight: 800;
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
`;

const FeatureCopy = styled.div`
  flex: 1;
  min-width: 240px;
`;

const FeatureName = styled.div`
  color: #111827;
  font-size: 0.92rem;
  font-weight: 800;
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.65 : 1};
`;

const ToggleInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const ToggleTrack = styled.span`
  width: 54px;
  height: 30px;
  border-radius: 999px;
  background: ${props => props.$checked ? '#2c9fa4' : '#cbd5e1'};
  position: relative;
  transition: background 0.2s ease;
  flex-shrink: 0;
`;

const ToggleThumb = styled.span`
  position: absolute;
  top: 3px;
  left: ${props => props.$checked ? '27px' : '3px'};
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.2);
  transition: left 0.2s ease;
`;

const ToggleText = styled.span`
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 800;
  min-width: 86px;
`;

const Nav = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const NavButton = styled.button`
  border: 1px solid ${props => props.$active ? '#2c9fa4' : '#cbd5e1'};
  background: ${props => props.$active ? '#2c9fa4' : '#ffffff'};
  color: ${props => props.$active ? '#ffffff' : '#0f172a'};
  border-radius: 999px;
  padding: 10px 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    border-color: #2c9fa4;
  }
`;

const HelperText = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.55;
`;

const StatusText = styled.p`
  margin: 0;
  color: ${props => props.$tone === 'error' ? '#b91c1c' : '#475569'};
  font-size: 0.85rem;
  line-height: 1.5;
`;

const PROJECT_BUILDER_ADMIN_VIEWS = [
  {
    id: 'resizer',
    label: 'Resizer',
    icon: SlidersHorizontal,
    helper: 'Adjust how board and module SVGs are scaled inside Builder.',
  },
  {
    id: 'passives',
    label: 'Basic Parts',
    icon: CircuitBoard,
    helper: 'Manage the basic parts catalog used by the public Builder.',
  },
  {
    id: 'examples',
    label: 'Examples',
    icon: Upload,
    helper: 'Upload ready-made Builder circuits and the code that should load with them.',
  },
];

const DEFAULT_PROJECT_BUILDER_SETTINGS = {
  arduino_simulation_enabled: false,
};

const normalizeProjectBuilderSettings = (payload) => {
  const rawValue = payload?.arduino_simulation_enabled ?? payload?.arduinoSimulationEnabled;

  return {
    arduino_simulation_enabled:
      rawValue === true ||
      rawValue === 1 ||
      rawValue === '1',
  };
};

const ProjectBuilderAdminPanel = ({
  boards,
  boardsLoading,
  onBoardScaleSaved,
}) => {
  const [activeView, setActiveView] = useState('resizer');
  const [projectBuilderSettings, setProjectBuilderSettings] = useState(DEFAULT_PROJECT_BUILDER_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsStatus, setSettingsStatus] = useState('');
  const activeConfig = PROJECT_BUILDER_ADMIN_VIEWS.find((view) => view.id === activeView);

  useEffect(() => {
    let isMounted = true;

    const loadProjectBuilderSettings = async () => {
      try {
        setSettingsLoading(true);
        setSettingsError('');

        const response = await adminProjectBuilderAPI.getSettings();
        if (!isMounted) {
          return;
        }

        setProjectBuilderSettings(normalizeProjectBuilderSettings(response.data));
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setSettingsError(
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load Builder feature settings.'
        );
      } finally {
        if (isMounted) {
          setSettingsLoading(false);
        }
      }
    };

    loadProjectBuilderSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleArduinoSimulationToggle = async () => {
    if (settingsLoading || settingsSaving) {
      return;
    }

    const previousSettings = projectBuilderSettings;
    const nextSettings = {
      ...projectBuilderSettings,
      arduino_simulation_enabled: !projectBuilderSettings.arduino_simulation_enabled,
    };

    setProjectBuilderSettings(nextSettings);
    setSettingsSaving(true);
    setSettingsError('');
    setSettingsStatus(nextSettings.arduino_simulation_enabled ? 'Enabling simulator...' : 'Disabling simulator...');

    try {
      const response = await adminProjectBuilderAPI.updateSettings(nextSettings);
      const savedSettings = normalizeProjectBuilderSettings(response?.data?.data || response?.data);

      setProjectBuilderSettings(savedSettings);
      if (savedSettings.arduino_simulation_enabled) {
        setSettingsStatus('Arduino simulation is now visible. Refreshing the page...');

        if (typeof window !== 'undefined') {
          window.setTimeout(() => {
            window.location.reload();
          }, 150);
        }

        return;
      }

      setSettingsStatus('Arduino simulation is now hidden from the public Builder.');
    } catch (error) {
      setProjectBuilderSettings(previousSettings);
      setSettingsStatus('');
      setSettingsError(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to save Builder feature settings.'
      );
    } finally {
      setSettingsSaving(false);
    }
  };

  return (
    <Wrapper>
      <FeaturePanel>
        <FeatureHeader>
          <div>
            <FeatureTitle>
              <Code2 size={18} />
              Hidden Feature Switches
            </FeatureTitle>
{/*             <FeatureDescription>
              Keep new Builder tools private until we are ready to expose them. This simulator toggle starts off disabled.
            </FeatureDescription> */}
          </div>

          <FeatureStateBadge $enabled={projectBuilderSettings.arduino_simulation_enabled}>
            {projectBuilderSettings.arduino_simulation_enabled ? 'Enabled' : 'Disabled'}
          </FeatureStateBadge>
        </FeatureHeader>

        <FeatureRow>
          <FeatureCopy>
            <FeatureName>Arduino simulation</FeatureName>
{/*             <FeatureHint>
              When enabled, Builder shows a simulator tool button with a code editor and Run button. The execution backend can be connected later.
            </FeatureHint> */}
          </FeatureCopy>

          <ToggleLabel $disabled={settingsLoading || settingsSaving}>
            <ToggleInput
              type="checkbox"
              checked={projectBuilderSettings.arduino_simulation_enabled}
              onChange={handleArduinoSimulationToggle}
              disabled={settingsLoading || settingsSaving}
            />
            <ToggleTrack $checked={projectBuilderSettings.arduino_simulation_enabled}>
              <ToggleThumb $checked={projectBuilderSettings.arduino_simulation_enabled} />
            </ToggleTrack>
            <ToggleText>
              {settingsLoading ? 'Loading...' : settingsSaving ? 'Saving...' : projectBuilderSettings.arduino_simulation_enabled ? 'Turn Off' : 'Turn On'}
            </ToggleText>
          </ToggleLabel>
        </FeatureRow>

        {settingsError ? (
          <StatusText $tone="error">{settingsError}</StatusText>
        ) : settingsStatus ? (
          <StatusText>{settingsStatus}</StatusText>
        ) : (
          <StatusText>
            {/* Disabled by default so the public builder stays unchanged until you deliberately switch it on. */}
          </StatusText>
        )}
      </FeaturePanel>

      <Nav>
        {PROJECT_BUILDER_ADMIN_VIEWS.map((view) => {
          const Icon = view.icon;

          return (
            <NavButton
              key={view.id}
              type="button"
              $active={activeView === view.id}
              onClick={() => setActiveView(view.id)}
            >
              <Icon size={16} />
              {view.label}
            </NavButton>
          );
        })}
      </Nav>

      <HelperText>{activeConfig?.helper}</HelperText>

      {activeView === 'resizer' ? (
        <ProjectBuilderBoardSizer
          boards={boards}
          boardsLoading={boardsLoading}
          onBoardScaleSaved={onBoardScaleSaved}
        />
      ) : activeView === 'examples' ? (
        <ProjectBuilderExamplesManager />
      ) : (
        <ProjectBuilderPassiveManager />
      )}
    </Wrapper>
  );
};

export default ProjectBuilderAdminPanel;
